import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export const APP_FEEDBACK_QUERY_KEY = ['app-feedback'] as const

/** Safe client message when feedback insert fails (rate limits vs generic). */
export function appFeedbackSubmitErrorMessage(err: unknown): string {
  const raw =
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
      ? (err as { message: string }).message.trim()
      : ''
  if (
    raw.includes('Daily feedback limit') ||
    (raw.includes('wait') && raw.includes('minutes') && raw.includes('feedback'))
  ) {
    return raw
  }
  if (raw === 'Not signed in' || raw === 'Empty feedback') return raw
  return 'Could not send feedback. Try again later.'
}

export function useSubmitAppFeedback() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      body,
      contextPath,
    }: {
      body: string
      contextPath: string | null
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not signed in')
      const trimmed = body.trim()
      if (!trimmed.length) throw new Error('Empty feedback')
      const { error } = await supabase.from('app_feedback').insert({
        user_id: user.id,
        body: trimmed,
        context_path: contextPath?.trim() || null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...APP_FEEDBACK_QUERY_KEY, 'admin'] })
    },
  })
}

export type AdminAppFeedbackRow = {
  id: string
  user_id: string
  profile_id: string | null
  username: string
  body: string
  context_path: string | null
  created_at: string
}

export function useAdminAppFeedbackList(enabled = true) {
  return useQuery({
    queryKey: [...APP_FEEDBACK_QUERY_KEY, 'admin'],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('app_feedback')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500)

      if (error) throw error
      if (!rows?.length) return [] as AdminAppFeedbackRow[]

      const uids = [...new Set(rows.map((r) => r.user_id))]
      const { data: profs, error: e2 } = await supabase
        .from('profiles')
        .select('id, user_id, username')
        .in('user_id', uids)

      if (e2) throw e2
      const profByUid = Object.fromEntries((profs ?? []).map((p) => [p.user_id, p]))

      return rows.map((r) => {
        const p = profByUid[r.user_id]
        return {
          ...r,
          profile_id: p?.id ?? null,
          username: p?.username ?? r.user_id.slice(0, 8),
        }
      }) as AdminAppFeedbackRow[]
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  })
}

export function useDeleteAppFeedback() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('app_feedback').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...APP_FEEDBACK_QUERY_KEY, 'admin'] })
    },
  })
}
